/**
 * The Cultural Intel report (GET /cultural-intel/{id}/report, already validated) → the model the
 * report components read. The BE shape lives here only. The cards are light (no demand / audience /
 * advertising sections): the drawer loads those per trend (utils/useTrendDetail.js).
 *
 * The BE sends the ML's lists in the ML's order: `relevantTrends` (the top trends), the culture
 * blocks, and the global trends. The top trends are re-ordered here by Opportunity score (highest
 * first); the culture blocks and global trends keep the ML's order. A trend can sit in several places
 * with the same `id`.
 */

import { TREND_STATUS_META } from './culturalIntelligenceTokens';

// The moment's cards, then the focus map; the rest of the relevant trends are "Worth watching".
const MOMENT_COUNT = 3;
const MAPPED_COUNT = 10;

// Section copy the BE doesn't send.
const MOMENT_TITLE = 'Your top 3 trends to tackle. Other relevant trends below.';
const RELEVANT_TRENDS_COPY = {
  title: 'Your top 10 trends analyzed',
  description:
    'See which trends fit your product best and how widely they’re spreading, so you can decide where to act first.',
};

// The trend type (`trendType`; older payloads sent it as `status`). The ML's stage names don't all map
// onto the four types yet (details doc, open question 4), so anything unknown reads as emerging
// rather than failing the report.
const statusOf = (card) => {
  const type = card.trendType ?? card.status;
  return type && TREND_STATUS_META[type] ? type : 'emerging';
};

const slugOf = (name) =>
  String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-08-01" → "Aug 2026", read from the string so no time zone shifts the month. */
const monthLabelOf = (date) => {
  const [year, month] = String(date).split('-');
  return MONTHS[Number(month) - 1] ? `${MONTHS[Number(month) - 1]} ${year}` : String(date);
};

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * The card's monthly `trajectory` (oldest first) → the sparkline's `[{ label, value }]`. Which field
 * to plot isn't decided by the BE yet (details doc, open question 9): search volume when the ML sent
 * it for every month, else `magnitude` (0–1) as a 0–100 interest score like relevance and reach.
 * Fewer than two points draw no line.
 */
const seriesOf = (card) => {
  const points = (Array.isArray(card.trajectory) ? card.trajectory : []).filter(
    (point) => point?.date
  );
  const bySearches = points.length > 0 && points.every((point) => isNumber(point.searchVolume));
  const valueOf = (point) => {
    if (bySearches) return point.searchVolume;
    return isNumber(point.magnitude) ? Math.round(point.magnitude * 100) : null;
  };
  const series = points
    .map((point) => ({ label: monthLabelOf(point.date), value: valueOf(point) }))
    .filter((point) => point.value != null);
  return series.length >= 2
    ? { series, seriesMetric: bySearches ? 'Searches' : 'Interest' }
    : { series: undefined, seriesMetric: undefined };
};

/**
 * The fields every list shows, and the interest series. The two scores the UI shows are `opportunity`
 * (the BE's `opportunityScore`) and `momentum`, both 0–100 or null; a null score shows as "—", never
 * another score in its place. `relevance` / `reach` stay for ranking only.
 */
const baseTrend = (card, rank) => ({
  id: card.id,
  rank,
  status: statusOf(card),
  category: card.culture ?? '',
  title: card.title,
  description: card.description ?? '',
  relevance: card.relevance ?? 0,
  reach: card.trendReach ?? 0,
  opportunity: card.opportunityScore ?? null,
  momentum: card.momentum ?? null,
  ...seriesOf(card),
});

/**
 * The top trends by Opportunity score, highest first; trends without one go last. The sort is stable,
 * so ties and the trends without a score keep the ML's order.
 */
const byOpportunity = (cards) =>
  [...cards].sort((a, b) => {
    const scoreA = isNumber(a.opportunityScore) ? a.opportunityScore : -Infinity;
    const scoreB = isNumber(b.opportunityScore) ? b.opportunityScore : -Infinity;
    if (scoreA === scoreB) return 0;
    return scoreB > scoreA ? 1 : -1;
  });

/**
 * @param {import('../../../schemas/culturalIntelligence').CulturalIntelReport} data
 * @param {{ completedAt?: string|null }} [status] GET /cultural-intel/{id}, for the last-updated date
 */
export default function adaptCulturalReport(data, status = {}) {
  // Ranks (the cards' 1–3, the map's badges, Worth watching, "In your report · #N") follow this order.
  const top = byOpportunity(data.relevantTrends);
  const topRanks = new Map(top.map((card, index) => [card.id, index + 1]));
  const summary = data.verdict.summaryParts.map(({ text }) => text).join('');

  return {
    responseId: data.culturalIntelResponseId,
    period: data.period,
    brandName: data.subject.name,
    lastUpdatedAt: status?.completedAt ?? null,

    // The verdict's summary card (headline + text), then the top trend cards.
    moment: {
      title: data.verdict.headline || MOMENT_TITLE,
      description: summary,
      topTrends: top.slice(0, MOMENT_COUNT).map((card, index) => baseTrend(card, index + 1)),
    },

    relevantTrends: {
      ...RELEVANT_TRENDS_COPY,
      mapped: top.slice(0, MAPPED_COUNT).map((card, index) => baseTrend(card, index + 1)),
      watching: top
        .slice(MAPPED_COUNT)
        .map((card, index) => baseTrend(card, MAPPED_COUNT + index + 1)),
    },

    // Each culture's relevant trends first, then the others, ranked by that order. A trend that is
    // also a top trend points at it (`reportTrendId`), which marks it "In your report".
    cultures: data.trendsByCultures.map((block) => ({
      id: slugOf(block.name),
      name: block.name,
      relevance: block.cultureRelevance ?? 0,
      trends: [...block.relevantTrends, ...block.otherTrends].map((card, index) => ({
        ...baseTrend(card, index + 1),
        reportTrendId: topRanks.has(card.id) ? card.id : undefined,
      })),
    })),

    globalTrends: data.globalTrends.map((card, index) => ({
      ...baseTrend(card, index + 1),
      // Global trends may carry no relevance (null from the BE); their score is `opportunityScore`.
      relevance: card.relevance,
      stats: {
        relevance: card.relevance,
        opportunity: card.opportunityScore ?? null,
        momentum: card.momentum,
        acceleration: card.acceleration,
      },
    })),
  };
}

/**
 * Every card's interpretation, by trend id (the same trend has the same interpretation wherever it
 * shows): `{ [trendId]: { id, status } }`.
 */
export const interpretationsOf = (data) => {
  const cards = [
    ...data.relevantTrends,
    ...data.trendsByCultures.flatMap((block) => [...block.relevantTrends, ...block.otherTrends]),
    ...data.globalTrends,
  ];
  return cards.reduce((acc, card) => {
    if (card.interpretation) acc[card.id] = card.interpretation;
    return acc;
  }, {});
};
